import type { ComponentProps } from "react";
import { cn } from "../lib/cn";

export function Input({ className, ...props }: ComponentProps<"input">) {
  return (
    <input
      className={cn(
        "h-9 w-full border border-line-strong bg-surface px-3 text-base text-ink transition-colors duration-150 ease-out-quick",
        "placeholder:text-ink-4 hover:border-ink-3 focus-visible:border-ink focus-visible:outline-none",
        "disabled:bg-surface-2 disabled:text-ink-3 aria-invalid:border-danger",
        className,
      )}
      {...props}
    />
  );
}

/** Multi-line sibling of Input: same border, type and states; grows only when the caller sets `rows`. */
export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return (
    <textarea
      className={cn(
        "w-full border border-line-strong bg-surface px-3 py-2 text-base leading-6 text-ink transition-colors duration-150 ease-out-quick",
        "placeholder:text-ink-4 hover:border-ink-3 focus-visible:border-ink focus-visible:outline-none",
        "disabled:bg-surface-2 disabled:text-ink-3 aria-invalid:border-danger",
        className,
      )}
      {...props}
    />
  );
}

/** A native select drawn like an Input, with a caret on the right so it reads as a list before it is opened. */
export function Select({ className, children, ...props }: ComponentProps<"select">) {
  return (
    <select
      className={cn(
        "h-9 w-full appearance-none border border-line-strong bg-surface pe-8 ps-3 text-base text-ink transition-colors duration-150 ease-out-quick",
        "bg-[image:var(--select-caret)] bg-[size:16px_16px] bg-[position:right_8px_center] bg-no-repeat rtl:bg-[position:left_8px_center]",
        "hover:border-ink-3 focus-visible:border-ink focus-visible:outline-none disabled:bg-surface-2 disabled:text-ink-3",
        className,
      )}
      {...props}
    >
      {children}
    </select>
  );
}

/**
 * The search a list page opens with: `/` anywhere on the page puts the cursor in it (`useListKeys`, mounted by every
 * app's shell), and the key shows at its end from `md` until the field is focused or holds text. `className` sizes the
 * field (`max-w-xs`); everything else goes to the input. One per page.
 */
export function SearchInput({ className, ...props }: Omit<ComponentProps<"input">, "type">) {
  return (
    <span className={cn("relative block w-full", className)}>
      <Input type="search" aria-keyshortcuts="/" className="peer pe-8" {...props} />
      <kbd
        aria-hidden
        className="pointer-events-none absolute inset-y-0 end-2 my-auto hidden h-5 items-center border border-line px-1.5 font-sans text-2xs text-ink-3 transition-opacity duration-150 ease-out-quick peer-focus:opacity-0 peer-[:not(:placeholder-shown)]:opacity-0 md:flex"
      >
        /
      </kbd>
    </span>
  );
}
